import * as Sentry from "@sentry/react";

/**
 * Issue #1529 — keep Sentry's `transaction` field honest across SPA navigation.
 *
 * ── The bug this exists to work around ─────────────────────────────────────
 *
 * A production event reported `[chat-persist] Failed to create conversation`
 * against transaction `/auth/callback`. That route cannot produce it: `Index`
 * mounts only at `/` (App.tsx), and the only conversation-create lives in
 * `sendMessage`, reached solely by a user tap. The label was fabricated, and it
 * sent a diagnosis down a "fetch racing the post-login redirect" path the code
 * disproves.
 *
 * Traced to `@sentry/browser` 10.71.0 (three linked facts, all verified in the
 * installed SDK — re-check them before deleting this file):
 *
 *   1. For NON-transaction events (errors, and the messages
 *      `captureConsoleIntegration` synthesises), `event.transaction` is read
 *      ONLY from `scope.transactionName` —
 *      `@sentry/core/.../utils/scopeData.js:90-91`. The root-span fallback a
 *      few lines below is gated on `event.type === "transaction"`, so no
 *      active span can repair a wrong name on an error event.
 *
 *   2. `scope.transactionName` is set at pageload, and updated on navigation
 *      by `startBrowserTracingNavigationSpan`.
 *
 *   3. Except the history handler bails before ever reaching it —
 *      `browserTracingIntegration.js:291`:
 *
 *        if (from === void 0 && startingUrl?.indexOf(to) !== -1) { ...; return }
 *
 *      `AuthCallback` leaves via `navigate("/", { replace: true })`. That is
 *      the FIRST history mutation of the page, so `from` is `undefined`
 *      (`lastHref` starts unset in `@sentry/browser-utils/.../history.js`).
 *      And `to` — "https://host/" — is a PREFIX SUBSTRING of the
 *      "https://host/auth/callback?token_hash=..." landing URL, so `indexOf`
 *      returns 0, not -1. It is a substring test standing in for an equality
 *      test.
 *
 * Net effect: no navigation span, `setTransactionName` never runs, and the name
 * stays frozen at the landing route for the WHOLE SESSION. Every error after an
 * email confirmation — the one flow every new user passes through — is filed
 * under `/auth/callback`.
 *
 * ⚠️ DO NOT delete this as redundant with `browserTracingIntegration()`. It
 * looks redundant; it is not, for the three reasons above. The guard only
 * misfires when the destination URL is a prefix of the landing URL, which is
 * exactly the shape of "deep landing page redirects to `/`" — so the common
 * case looks fine and hides it.
 *
 * ⚠️ Scope, deliberately: this repairs the transaction NAME only. It does not
 * create the missing navigation span, so span-level tracing for that first
 * redirect is still absent. Fixing that means adopting
 * `reactRouterV6BrowserTracingIntegration`, which changes span structure and is
 * a separate change.
 *
 * Matches what the SDK itself would have written: `location.pathname`, the same
 * value `browserTracingIntegration` uses for a navigation span's name.
 */
export function syncSentryRoute(pathname: string | null | undefined): void {
  // An empty name would CLEAR the field (`setTransactionName(undefined)`), which
  // is strictly worse than a stale one — a missing route tells triage nothing,
  // where a wrong one at least gets questioned.
  if (!pathname) return;
  Sentry.getCurrentScope().setTransactionName(pathname);
}
